/* global BigInt */

import { RoadSurfaceType } from "@strava/feature-global-wasm-mre";
import type { ActivitySection } from "@strava/feature-global-wasm-mre";

/**
 * Accepts a privacyStream (array of numbers) and returns an array of ActivitySection objects.
 *
 * Privacy stream data comes in the form of an array of numbers.
 * The values are either 0 or 2.
 *
 * 0 indicates that the corresponding latLng is visible, 2 indicates that it is hidden.
 *
 * Note: An edge is private if either vertex is private (non-zero)
 * 
 * ActivitySection objects have a particular surface type and a privateSection attribute.
 *
 * Generally, the sum of ActivitySection.numPolylineEdges values across all sections has to be
 * equal to the number of vertices of the line minus 1.
 */

export const getPrivacyZonesActivitySections = (
  privacyStream: number[]
): ActivitySection[] => {
  const EVERYONE = 0;
  const sections: ActivitySection[] = [];

  // No privacy stream data
  if (!privacyStream || privacyStream.length === 0) {
    return sections; // Return empty array
  }

  // Only one vertex means no edges to process
  if (privacyStream.length === 1) {
    return sections; // Return empty array
  }

  // No private sections if all vertices are visible
  if (privacyStream.every((value) => value === EVERYONE)) {
    return sections; // Return empty array
  }

  // Process each edge (between consecutive vertices)
  for (let i = 0; i < privacyStream.length - 1; i++) {
    // Determine if this edge is in a private section
    // An edge is private if either vertex is private (non-zero)
    const isPrivateEdge = privacyStream[i] !== EVERYONE || privacyStream[i + 1] !== EVERYONE;

    // Get the current section (last one in the array)
    const currentSection = sections[sections.length - 1];

    // If this is the first edge or privacy state has changed, create a new section
    if (!currentSection || currentSection.privateSection !== isPrivateEdge) {
      sections.push({
        numPolylineEdges: BigInt(1),
        privateSection: isPrivateEdge,
        surfaceType: RoadSurfaceType.PAVED
      });
    } else {
      // Same privacy state as current section, increment edge count
      currentSection.numPolylineEdges += BigInt(1);
    }
  }

  return sections;
};

export default getPrivacyZonesActivitySections;
